export interface WhisperApiResponse {
  text: string;
}

export interface WhisperApiError {
  message: string;
  code?: string;
  status?: number;
}

export interface TranscribeOptions {
  apiKey?: string;
  backendUrl?: string;
}

export const transcribeAudio = async (
  audioBlob: Blob,
  options?: TranscribeOptions
): Promise<string> => {
  const { apiKey, backendUrl } = options || {};

  const fileName = audioBlob.type.includes("webm")
    ? "audio.webm"
    : audioBlob.type.includes("mp4")
    ? "audio.mp4"
    : audioBlob.type.includes("wav")
    ? "audio.wav"
    : audioBlob.type.includes("ogg")
    ? "audio.ogg"
    : "audio.webm";

  const formData = new FormData();
  formData.append("file", audioBlob, fileName);

  if (backendUrl) {
    return transcribeViaBackend(formData, backendUrl);
  }

  return transcribeViaOpenAI(formData, apiKey);
};

const getAuthToken = (): string | null => {
  const tokenFromStorage = localStorage.getItem("authToken");
  if (tokenFromStorage) {
    return tokenFromStorage;
  }

  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const prefersStgToken = hostname.includes("stg.ippon-cloud.com");
  const cookieCandidates = prefersStgToken
    ? ["stg_token", "token"]
    : ["token", "stg_token"];

  const cookieMap = document.cookie
    .split(";")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, entry) => {
      const separatorIndex = entry.indexOf("=");
      if (separatorIndex <= 0) return acc;
      const key = entry.slice(0, separatorIndex);
      const value = entry.slice(separatorIndex + 1);
      acc[key] = value;
      return acc;
    }, {});

  for (const name of cookieCandidates) {
    if (cookieMap[name]) {
      return cookieMap[name];
    }
  }
  return null;
};

const transcribeViaBackend = async (
  formData: FormData,
  backendUrl: string
): Promise<string> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    // FormDataを送信する場合、Content-Typeは設定しない（ブラウザが自動設定）

    const response = await fetch(backendUrl, {
      method: "POST",
      headers,
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: `HTTP ${response.status} エラー` };
      }

      throw {
        message: errorData.message || `APIリクエストに失敗しました (${response.status})`,
        code: errorData.code,
        status: response.status,
      } as WhisperApiError;
    }

    const data: WhisperApiResponse = await response.json();

    if (!data.text || data.text.trim() === "") {
      throw new Error("文字起こし結果が空でした。");
    }

    return data.text.trim();
  } catch (error: any) {
    if (error.name === "AbortError") {
      throw {
        message: "リクエストがタイムアウトしました。もう一度お試しください。",
        code: "TIMEOUT_ERROR",
      } as WhisperApiError;
    }

    if (error.message && error.code) {
      throw error;
    }

    // fetch のネットワークレベル失敗は "TypeError: Failed to fetch"(英語) を投げる。
    // そのまま出すと英語表示になるため、日本語メッセージに置き換える。
    const isNetworkError =
      error instanceof TypeError ||
      (typeof error?.message === "string" &&
        error.message.toLowerCase().includes("failed to fetch"));

    throw {
      message: isNetworkError
        ? "音声の文字起こしに失敗しました。通信環境をご確認のうえ、もう一度お試しください。"
        : error.message || "音声の文字起こしに失敗しました",
      code: isNetworkError ? "NETWORK_ERROR" : "UNKNOWN_ERROR",
    } as WhisperApiError;
  }
};

const transcribeViaOpenAI = async (
  formData: FormData,
  apiKey?: string
): Promise<string> => {
  formData.append("model", "whisper-1");
  formData.append("language", "ja");
  formData.append("prompt", "これは日本語の音声入力です。");

  if (!apiKey) {
    throw new Error("APIキーが設定されていません。");
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    const response = await fetch(
      "https://api.openai.com/v1/audio/transcriptions",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${apiKey}`,
        },
        body: formData,
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { error: { message: `HTTP ${response.status} エラー` } };
      }

      throw {
        message: errorData.error?.message || `APIリクエストに失敗しました (${response.status})`,
        code: errorData.error?.code,
        status: response.status,
      } as WhisperApiError;
    }

    const data: WhisperApiResponse = await response.json();

    if (!data.text || data.text.trim() === "") {
      throw new Error("文字起こし結果が空でした。");
    }

    return data.text.trim();
  } catch (error: any) {
    if (error.name === "AbortError") {
      throw {
        message: "リクエストがタイムアウトしました。もう一度お試しください。",
        code: "TIMEOUT_ERROR",
      } as WhisperApiError;
    }

    if (error.message && error.code) {
      throw error;
    }

    // fetch のネットワークレベル失敗は "TypeError: Failed to fetch"(英語) を投げる。
    // そのまま出すと英語表示になるため、日本語メッセージに置き換える。
    const isNetworkError =
      error instanceof TypeError ||
      (typeof error?.message === "string" &&
        error.message.toLowerCase().includes("failed to fetch"));

    throw {
      message: isNetworkError
        ? "音声の文字起こしに失敗しました。通信環境をご確認のうえ、もう一度お試しください。"
        : error.message || "音声の文字起こしに失敗しました",
      code: isNetworkError ? "NETWORK_ERROR" : "UNKNOWN_ERROR",
    } as WhisperApiError;
  }
};
